// src/components/dnl/RatioTableOverlay.tsx
/**
 * Ratio Table Overlay Component
 * Phase 7: Ratio Table Overlay
 * 
 * Draggable table showing value pairs synchronized with DNL custom points
 * Based on DNL Enhancement Specification v1.0, Section 3.7
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GripVertical,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  X,
} from 'lucide-react';
import { RootState } from '../../types/redux';
import {
  updateTableCell,
  toggleCellVisibility,
  addTableColumn,
  removeTableColumn,
  syncTableWithCustomTicks,
  setTableCustomPosition,
  setTableDragging,
  toggleRatioTable,
} from '../../store/slices/dnlToolsSlice';
import { formatNumberWithConfig } from '../../lib/numberFormatting';
import type { RatioTableCell } from '../../types/dnl';

// =============================================================================
// TYPES
// =============================================================================

export interface RatioTableOverlayProps {
  /** Container bounds for constraining drag */
  containerRef: React.RefObject<HTMLElement>;
  /** Dark mode */
  isDark?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const RatioTableOverlay: React.FC<RatioTableOverlayProps> = ({
  containerRef,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  
  const ratioTable = useSelector((state: RootState) => state.dnlTools.ratioTable);
  const primaryLabel = useSelector((state: RootState) => state.dnlTools.primaryLabel);
  const secondaryLabel = useSelector((state: RootState) => state.dnlTools.secondaryLabel);
  const primaryFormat = useSelector((state: RootState) => state.numberLines.numberFormat);
  const secondaryFormat = useSelector((state: RootState) => state.secondaryLine.numberFormat);
  
  const tableRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState(ratioTable.customPosition);
  
  // Initialize position based on position mode
  useEffect(() => {
    if (!containerRef.current || !tableRef.current) return;
    
    const container = containerRef.current.getBoundingClientRect();
    const table = tableRef.current.getBoundingClientRect();
    
    if (ratioTable.positionMode !== 'custom') {
      let x = 0;
      let y = 0;
      
      switch (ratioTable.positionMode) {
        case 'top-left':
          x = 20;
          y = 20;
          break;
        case 'top-right':
          x = container.width - table.width - 20;
          y = 20;
          break;
        case 'bottom-left':
          x = 20;
          y = container.height - table.height - 20;
          break;
        case 'bottom-right':
          x = container.width - table.width - 20;
          y = container.height - table.height - 20;
          break;
      }
      
      setPosition({ x, y });
    }
  }, [ratioTable.positionMode, containerRef]);
  
  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!tableRef.current) return;
    
    const rect = tableRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
    dispatch(setTableDragging(true));
  }, [dispatch]);
  
  // Handle drag move
  useEffect(() => {
    if (!isDragging || !containerRef.current || !tableRef.current) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current!.getBoundingClientRect();
      const table = tableRef.current!.getBoundingClientRect();
      
      let newX = e.clientX - container.left - dragOffset.x;
      let newY = e.clientY - container.top - dragOffset.y;
      
      // Constrain to container bounds
      newX = Math.max(0, Math.min(newX, container.width - table.width));
      newY = Math.max(0, Math.min(newY, container.height - table.height));
      
      setPosition({ x: newX, y: newY });
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      dispatch(setTableDragging(false));
      dispatch(setTableCustomPosition(position));
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position, dispatch, containerRef]);
  
  // Handle cell value change
  const handleCellChange = useCallback((cellId: string, newValue: string) => {
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      dispatch(updateTableCell({ cellId, value: numValue }));
    } else if (newValue === '') {
      dispatch(updateTableCell({ cellId, value: null }));
    }
  }, [dispatch]);
  
  // Get cells organized by row and column
  const getCellsForRow = (row: number) => {
    return ratioTable.cells
      .filter(cell => cell.row === row)
      .sort((a, b) => a.column - b.column);
  };
  
  const primaryCells = getCellsForRow(0);
  const secondaryCells = getCellsForRow(1);
  
  // Format cell value
  const formatCellValue = (cell: RatioTableCell) => {
    if (cell.value === null || cell.value === undefined) return '';
    const format = cell.row === 0 ? primaryFormat : secondaryFormat;
    return formatNumberWithConfig(cell.value, format, 'en-GB');
  };
  
  if (!ratioTable.visible) return null;
  
  return (
    <div
      ref={tableRef}
      className={`absolute z-50 rounded-lg shadow-2xl border-2 ${
        isDragging ? 'cursor-move' : ''
      } ${
        isDark
          ? 'bg-gray-800 border-gray-600'
          : 'bg-white border-gray-300'
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        minWidth: '300px',
      }}
    >
      {/* Header with drag handle */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b cursor-move ${
          isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-2">
          <GripVertical size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ratio Table
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => dispatch(syncTableWithCustomTicks())}
            className={`p-1 rounded transition-colors ${
              isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
            }`}
            title="Sync with custom points"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => dispatch(toggleRatioTable())}
            className={`p-1 rounded transition-colors ${
              isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
            }`}
            title="Close table"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={isDark ? 'bg-gray-750' : 'bg-gray-100'}>
              <th className={`px-3 py-2 text-left text-xs font-medium ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {primaryLabel.text || 'Primary'}
              </th>
              {primaryCells.map((cell, idx) => (
                <th
                  key={cell.id}
                  className={`px-2 py-2 text-center text-xs ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <button
                    onClick={() => dispatch(removeTableColumn(cell.column))}
                    className={`p-0.5 rounded hover:bg-red-500/20 transition-colors`}
                    title="Remove column"
                  >
                    <Trash2 size={12} className="text-red-500" />
                  </button>
                </th>
              ))}
              <th className="px-2 py-2 text-center">
                <button
                  onClick={() => dispatch(addTableColumn())}
                  className={`p-1 rounded transition-colors ${
                    isDark
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                  title="Add column"
                >
                  <Plus size={12} className="text-white" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Primary Row */}
            <tr className={isDark ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
              <td className={`px-3 py-2 text-xs font-medium ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {primaryLabel.text || 'Primary'}
              </td>
              {primaryCells.map(cell => (
                <td key={cell.id} className="px-2 py-2">
                  {cell.visible ? (
                    <input
                      type="number"
                      value={cell.value ?? ''}
                      onChange={e => handleCellChange(cell.id, e.target.value)}
                      disabled={!cell.editable}
                      className={`w-full px-2 py-1 text-sm text-center rounded border ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      } ${!cell.editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                      step="any"
                    />
                  ) : (
                    <div className={`w-full px-2 py-1 text-sm text-center ${
                      isDark ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      ?
                    </div>
                  )}
                </td>
              ))}
              <td></td>
            </tr>
            
            {/* Secondary Row */}
            <tr>
              <td className={`px-3 py-2 text-xs font-medium ${
                isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {secondaryLabel.text || 'Secondary'}
              </td>
              {secondaryCells.map(cell => (
                <td key={cell.id} className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    {cell.visible ? (
                      <input
                        type="number"
                        value={cell.value ?? ''}
                        onChange={e => handleCellChange(cell.id, e.target.value)}
                        disabled={!cell.editable}
                        className={`flex-1 px-2 py-1 text-sm text-center rounded border ${
                          isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        } ${!cell.editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                        step="any"
                      />
                    ) : (
                      <div className={`flex-1 px-2 py-1 text-sm text-center ${
                        isDark ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        ?
                      </div>
                    )}
                    <button
                      onClick={() => dispatch(toggleCellVisibility(cell.id))}
                      className={`p-1 rounded transition-colors ${
                        isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                      }`}
                      title={cell.visible ? 'Hide cell' : 'Show cell'}
                    >
                      {cell.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                    </button>
                  </div>
                </td>
              ))}
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Footer with info */}
      <div className={`px-3 py-2 text-xs border-t ${
        isDark
          ? 'text-gray-400 border-gray-700 bg-gray-750'
          : 'text-gray-500 border-gray-200 bg-gray-50'
      }`}>
        {ratioTable.autoSync ? (
          '🔄 Auto-synced with custom points'
        ) : (
          '⚠️ Manual mode - sync to update'
        )}
      </div>
    </div>
  );
};

export default RatioTableOverlay;






